import type { OpeningPeriod, OpeningPeriodPoint } from '@bilmat/shared';
import { formatClock } from './format';

const WEEK_MIN = 7 * 24 * 60;
const DAY_MIN = 24 * 60;

export type OpeningStatus =
  | { kind: 'unknown' }
  | { kind: 'always-open' }
  | { kind: 'open'; minutesToClose: number; closeAt: Date }
  | { kind: 'closed'; nextOpenAt: Date | null };

function timeToMinutes(time: string): number {
  // Google-format "HHMM", t.ex. "0930" eller "2200".
  const hh = Number(time.slice(0, 2));
  const mm = Number(time.slice(2, 4));
  return hh * 60 + mm;
}

function pointToWeekMinutes(point: OpeningPeriodPoint): number {
  return point.day * DAY_MIN + timeToMinutes(point.time);
}

/**
 * Avgör öppet-status vid en given framkomsttidpunkt utifrån Googles `periods`.
 * Tiderna tolkas i enhetens lokala tid (appen är Sverige-only).
 */
export function openingStatusAt(periods: OpeningPeriod[], arrival: Date): OpeningStatus {
  if (periods.length === 0) return { kind: 'unknown' };
  // Googles representation av "öppet dygnet runt": en period utan stängning.
  if (periods.length === 1 && periods[0]?.close == null) return { kind: 'always-open' };

  const arrivalMin = arrival.getDay() * DAY_MIN + arrival.getHours() * 60 + arrival.getMinutes();

  let minutesToClose: number | null = null;
  let nextOpenDelta: number | null = null;

  for (const period of periods) {
    if (period.close == null) continue;
    const openMin = pointToWeekMinutes(period.open);
    let closeMin = pointToWeekMinutes(period.close);
    if (closeMin <= openMin) closeMin += WEEK_MIN; // över midnatt / vecko-wrap

    // Testa både innevarande vecka och föregående veckas wrap.
    for (const a of [arrivalMin, arrivalMin + WEEK_MIN]) {
      if (a >= openMin && a < closeMin) {
        const toClose = closeMin - a;
        if (minutesToClose == null || toClose < minutesToClose) minutesToClose = toClose;
      }
    }
    // Närmaste kommande öppning (positivt avstånd).
    for (const o of [openMin, openMin + WEEK_MIN]) {
      const delta = o - arrivalMin;
      if (delta > 0 && (nextOpenDelta == null || delta < nextOpenDelta)) nextOpenDelta = delta;
    }
  }

  if (minutesToClose != null) {
    return {
      kind: 'open',
      minutesToClose,
      closeAt: new Date(arrival.getTime() + minutesToClose * 60_000),
    };
  }
  return {
    kind: 'closed',
    nextOpenAt: nextOpenDelta != null ? new Date(arrival.getTime() + nextOpenDelta * 60_000) : null,
  };
}

const WEEKDAYS = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];

/** "Öppnar 11:00" / "Öppnar imorgon 11:00" / "Öppnar lördag 10:00". */
export function formatNextOpen(arrival: Date, nextOpenAt: Date): string {
  const a = new Date(arrival.getFullYear(), arrival.getMonth(), arrival.getDate());
  const o = new Date(nextOpenAt.getFullYear(), nextOpenAt.getMonth(), nextOpenAt.getDate());
  const dayDiff = Math.round((o.getTime() - a.getTime()) / (DAY_MIN * 60_000));
  const clock = formatClock(nextOpenAt);
  if (dayDiff <= 0) return `Öppnar ${clock}`;
  if (dayDiff === 1) return `Öppnar imorgon ${clock}`;
  return `Öppnar ${WEEKDAYS[nextOpenAt.getDay()]} ${clock}`;
}

/** Hur länge du hinner äta: "3h 9m" / "45 min". */
export function formatEatWindow(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
